import {
  PROCEDURES_PREOPERATIVE_DIAGNOSIS,
  PROCEDURES_POST_OPERATIVE_DIAGNOSIS,
  PROCEDURES_STATUS,
  PROCEDURES_ELB,
  PROCEDURES_PLAN,
} from '@constants/extension-codes';

import {
  formatFhirDate,
  getFhirDate,
  getFhirDateComponents,
} from '@utils/fhir';
import { formatAddress } from '@utils/format';

import type {
  Annotation,
  CodeableConcept,
  ProcedureModel,
  ProcedureModelInput,
  ProcedureStatus,
} from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type SurgeryValues = Pick<
  ProcedureRecord,
  | 'anesthesia'
  | 'anesthesiologist'
  | 'city'
  | 'complications'
  | 'country'
  | 'doctor'
  | 'elb'
  | 'facility'
  | 'followUp'
  | 'plan'
  | 'postOperativeDiagnosis'
  | 'practitioner'
  | 'preoperativeDiagnosis'
  | 'procedureDate'
  | 'procedureNotes'
  | 'selfReported'
  | 'visitReason'
> & {
  title: string;
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & SurgeryValues;

export class ProcedureRecord extends FhirResource {
  protected model: ProcedureModel;

  public constructor(model: ProcedureModel) {
    super(model);
    this.model = model;
  }

  public get recordType() {
    return this.model.__typename;
  }

  public get procedureName() {
    return this.model.code.text ?? '';
  }

  public set procedureName(value: string) {
    this.model = { ...this.model, code: { text: value } };
  }

  public get procedureDate() {
    return this.model.performedDateTime;
  }

  public set procedureDate(value: string | null | undefined) {
    this.model = { ...this.model, performedDateTime: value };
  }

  public get facility() {
    return this.model.locationResource?.map(({ name }) => name).join('/');
  }

  public get address() {
    return this.model.locationResource
      ?.map(({ address }) =>
        formatAddress(
          address?.line,
          address?.city,
          address?.postalCode,
          address?.state,
          address?.country
        )
      )
      .join('/');
  }

  public get city() {
    return this.model.locationResource
      ?.map(({ address }) => address?.city)
      .join('/');
  }

  public get country() {
    return this.model.locationResource
      ?.map(({ address }) => address?.country)
      .join('/');
  }

  public get practitioner() {
    return this.model.performer
      ?.filter(
        ({ actor }) =>
          actor.type === 'provider' ||
          actor.type === 'practitioner' ||
          actor.reference?.startsWith('Practitioner')
      )
      .map(({ actor }) => actor.display)
      .join('/');
  }

  public set practitioner(value: string | null | undefined) {
    if (value) {
      this.addOrReplacePerformer({
        display: value,
        types: ['practitioner', 'provider'],
      });
    } else {
      this.removePerformer(['practitioner', 'provider']);
    }
  }

  public get anesthesiologist() {
    return this.model.performer
      ?.filter(({ actor }) => actor.type === 'anesthesiologist')
      .map(({ actor }) => actor.display)
      .join('/');
  }

  public set anesthesiologist(value: string | null | undefined) {
    if (value) {
      this.addOrReplacePerformer({
        display: value,
        types: ['anesthesiologist'],
      });
    } else {
      this.removePerformer(['anesthesiologist']);
    }
  }

  public get doctor() {
    return this.model.performer
      ?.filter(({ actor }) => actor.type === 'doctor')
      .map(({ actor }) => actor.display)
      .join('/');
  }

  public set doctor(value: string | null | undefined) {
    if (value) {
      this.addOrReplacePerformer({ display: value, types: ['doctor'] });
    } else {
      this.removePerformer(['doctor']);
    }
  }

  public get visitReason() {
    return this.model.reasonCode?.map((c) => c.text).join('/');
  }

  public set visitReason(value: string | null | undefined) {
    const reasonCode: CodeableConcept[] | null = value
      ? [{ text: value }]
      : null;
    this.model = {
      ...this.model,
      reasonCode,
    };
  }

  public get procedureNotes() {
    return this.model.note?.map((n) => n?.text).join('/');
  }

  public set procedureNotes(value: string | null | undefined) {
    const note: Annotation[] | null = value ? [{ text: value }] : null;
    this.model = { ...this.model, note };
  }

  public get complications() {
    return this.model.complication?.map((c) => c?.text).join('/');
  }

  public set complications(value: string | null | undefined) {
    const complication: CodeableConcept[] | null = value
      ? [{ text: value }]
      : null;
    this.model = {
      ...this.model,
      complication,
    };
  }

  public get followUp() {
    return this.model.followUp?.map((c) => c?.text).join('/');
  }

  public set followUp(value: string | null | undefined) {
    const followUp: CodeableConcept[] | null = value ? [{ text: value }] : null;
    this.model = {
      ...this.model,
      followUp,
    };
  }

  public get anesthesia() {
    return this.model.usedCode?.map((c) => c?.text).join('/');
  }

  public set anesthesia(value: string | null | undefined) {
    const usedCode: CodeableConcept[] | null = value ? [{ text: value }] : null;
    this.model = {
      ...this.model,
      usedCode,
    };
  }

  public get preoperativeDiagnosis() {
    const extension = this.findExtension(PROCEDURES_PREOPERATIVE_DIAGNOSIS);
    return extension?.valueString;
  }

  public set preoperativeDiagnosis(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: PROCEDURES_PREOPERATIVE_DIAGNOSIS,
        valueString: value,
      });
    } else {
      this.removeExtension(PROCEDURES_PREOPERATIVE_DIAGNOSIS);
    }
  }

  public get postOperativeDiagnosis() {
    const extension = this.findExtension(PROCEDURES_POST_OPERATIVE_DIAGNOSIS);
    return extension?.valueString;
  }

  public set postOperativeDiagnosis(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: PROCEDURES_POST_OPERATIVE_DIAGNOSIS,
        valueString: value,
      });
    } else {
      this.removeExtension(PROCEDURES_POST_OPERATIVE_DIAGNOSIS);
    }
  }

  public get status() {
    const extension = this.findExtension(PROCEDURES_STATUS);
    return extension?.valueString;
  }

  public set status(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: PROCEDURES_STATUS,
        valueString: value,
      });
    } else {
      this.removeExtension(PROCEDURES_STATUS);
    }
  }

  public get elb() {
    const extension = this.findExtension(PROCEDURES_ELB);
    return extension?.valueString;
  }

  public set elb(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: PROCEDURES_ELB,
        valueString: value,
      });
    } else {
      this.removeExtension(PROCEDURES_ELB);
    }
  }

  public get plan() {
    const extension = this.findExtension(PROCEDURES_PLAN);
    return extension?.valueString;
  }

  public set plan(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: PROCEDURES_PLAN,
        valueString: value,
      });
    } else {
      this.removeExtension(PROCEDURES_PLAN);
    }
  }

  public getProcedureDate() {
    return getFhirDate(this.model.performedDateTime);
  }

  public getProcedureDateComponents() {
    return getFhirDateComponents(this.model.performedDateTime);
  }

  public getProcedureDateStr(showTime = false) {
    return formatFhirDate(this.model.performedDateTime, { showTime });
  }

  public getModelInput(values?: SurgeryValues): ProcedureModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.procedureName = values.title;
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    delete cleaned.locationResource;
    return {
      ...cleaned,
      status: cleaned.status as ProcedureStatus,
    };
  }

  private addOrReplacePerformer(performer: {
    display: string;
    types: string[];
  }) {
    if (!this.model.performer) {
      this.model = { ...this.model, performer: [] };
    }
    const performers = [...(this.model.performer ?? [])];
    const index = performers.findIndex(
      ({ actor }) => actor.type && performer.types.includes(actor.type)
    );
    if (index > -1) {
      performers[index] = {
        ...performers[index],
        actor: {
          display: performer.display,
          type: performer.types.pop(),
        },
      };
    } else {
      performers.push({
        actor: {
          display: performer.display,
          type: performer.types[0],
        },
      });
    }
    this.model = { ...this.model, performer: performers };
  }

  private removePerformer(types: string[]) {
    const performers = this.model.performer?.filter(
      ({ actor }) => actor.type && !types.includes(actor.type)
    );
    this.model = { ...this.model, performer: performers };
  }
}
